import { verticalState } from "widgets/utils";

import { SyntenyTypeEnum } from "../../openapi";
import { CANVAS_VISIBILITY_OFFSCREEN_MARGIN, GROUP_HEADER_HEIGHT } from "../../settings.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import type { MiniTrackGroups, WorkerMessage } from "../../types.ts";
import { getTrackTypeBySyntenyType } from "../../utils/type-conversion.ts";
import { getAllTracksHeight } from "../utils.ts";

// Use track id for the key
let isBusy = false;
let isWaiting = false;

export function sendSyntenyRenderMessage(syntenyWorker: Worker | undefined) {
  if (isBusy) {
    isWaiting = true;
    return;
  }

  isBusy = true;

  function done() {
    isBusy = false;

    // Worker being synchronous, as long as it returns, it should be done.
    syntenyWorker?.removeEventListener("message", done);

    if (isWaiting) {
      isWaiting = false;
      sendSyntenyRenderMessage(syntenyWorker);
    }
  }

  syntenyWorker?.addEventListener("message", done);

  const state = useGenomeViewerStore.getState();
  const visibleTracks = [...state.tracks.values()].filter((track) => track.isVisible);
  const visibleTrackGroups = new Map(
    [...state.trackGroups.entries()].filter(([_, trackGroup]) => trackGroup.graphics.isVisible),
  );
  const miniTracks = new Map();
  for (const track of visibleTracks) {
    miniTracks.set(track.trackId, {
      trackId: track.trackId,
      trackType: track.trackType,
      height: track.height,
      baseStart: track.baseStart,
      basePerPixel: track.basePerPixel,
      isVisible: track.isVisible,
      shiftX: Math.round((state.trackGroups.get(track.groupId)?.graphics.baseShift ?? 0) / (track.basePerPixel ?? 1)),
    });
  }

  const miniTrackGroups: MiniTrackGroups = new Map();

  for (const [groupId, trackGroup] of visibleTrackGroups.entries()) {
    miniTrackGroups.set(groupId, {
      trackIds: trackGroup.trackIds,
    });
  }

  let currentY = 0;
  let lastGroupId: string | undefined;

  const visibleTrackIds = visibleTracks
    .filter((track) => {
      if (track.groupId !== lastGroupId) {
        currentY += GROUP_HEADER_HEIGHT; // we jump to a new group
        lastGroupId = track.groupId;
      }

      const height = state.canvas!.height;

      const { bottomProgress, topProgress } = verticalState(state.scrollY, height, currentY, track.height);

      currentY += track.height;

      const topPixel = Math.round(topProgress * height);
      const bottomPixel = Math.round(bottomProgress * height);

      return (
        topPixel > -CANVAS_VISIBILITY_OFFSCREEN_MARGIN || bottomPixel < height + CANVAS_VISIBILITY_OFFSCREEN_MARGIN
      );
    })
    .map((track) => track.trackId);

  // We are currently debouncing the rendering to avoid unnecessary rendering
  // It was impossible to detect consistently when to draw it and we end up to draw too often and fulfilled the message queue.
  syntenyWorker?.postMessage({
    type: "render",
    width: state.canvas!.width,
    height: Math.min(state.canvas!.height, getAllTracksHeight()),
    scrollX: state.scrollX,
    scrollY: state.scrollY,
    tracks: miniTracks,
    trackGroups: miniTrackGroups,
    visibleTrackIds: visibleTrackIds,
    basePerPixel: state.basePerPixel,
    alignedFamily: state.alignedFamily,
    activeMemberId: state.activeMemberId,
    alignmentDensity: state.alignmentDensity,
    trackType: getTrackTypeBySyntenyType(state.activeSyntenyType ?? SyntenyTypeEnum.GENE),
  } as WorkerMessage["SyntenicAlignmentsWorker"]["Render"]);
}
